import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { MOBILE_SX } from '../../mobile/mobileApp';

/**
 * For a table whose outer columns are pinned: a scroller's own sideways bar runs
 * under every column though only the middle ones move, so it is hidden
 * (HIDDEN_X_SCROLLBAR) and PinnedScrollbar draws one under the moving columns alone.
 *
 * App.css sets scrollbar-width and scrollbar-color on everything, and Chrome ignores
 * ::-webkit-scrollbar once either is set. So the scroller puts both back to auto and
 * hides the sideways bar through the pseudo-element alone; the up-and-down bar stays,
 * styled by the global ::-webkit-scrollbar rules. Firefox cannot hide one axis, so it
 * keeps both thin bars and the pinned one doubles the sideways bar there.
 */
export const HIDDEN_X_SCROLLBAR = {
  scrollbarWidth: 'auto',
  scrollbarColor: 'auto',
  '&::-webkit-scrollbar:horizontal': { height: 0 },
  '@supports not selector(::-webkit-scrollbar)': { scrollbarWidth: 'thin' }
} as const;

const PinnedScrollbar: React.FC<{
  /** The scrolling element: an element rather than a ref, so this follows it as it mounts. */
  scroller: HTMLElement | null;
  /** How much is pinned on the left, and on the right. */
  left: number;
  right?: number;
}> = ({ scroller, left, right = 0 }) => {
  const bar = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ view: 0, overflow: 0 });

  useEffect(() => {
    if (!scroller) return undefined;
    const measure = () =>
      setSize({
        view: Math.max(0, scroller.clientWidth - left - right),
        overflow: Math.max(0, scroller.scrollWidth - scroller.clientWidth)
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    const follow = () => {
      if (bar.current && bar.current.scrollLeft !== scroller.scrollLeft) bar.current.scrollLeft = scroller.scrollLeft;
    };
    scroller.addEventListener('scroll', follow);
    return () => {
      observer.disconnect();
      scroller.removeEventListener('scroll', follow);
    };
  }, [scroller, left, right]);

  if (!size.overflow) return null;
  return (
    <Box
      ref={bar}
      onScroll={(e: React.UIEvent<HTMLDivElement>) => {
        if (scroller && scroller.scrollLeft !== e.currentTarget.scrollLeft) {
          scroller.scrollLeft = e.currentTarget.scrollLeft;
        }
      }}
      sx={{
        flexShrink: 0,
        ml: `${left}px`,
        width: size.view,
        height: 14,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
        scrollbarColor: '#565656 transparent',
        [MOBILE_SX]: { display: 'none' }
      }}
    >
      <Box sx={{ width: size.view + size.overflow, height: 1 }} />
    </Box>
  );
};

export default PinnedScrollbar;
